import { defineStore } from 'pinia';
import type { EventBusKey } from '@vueuse/core';
import { useIntentsStore } from '@/stores/intents';

/**
 * Pinia conversion of legacy vuex module `src/store/modules/aiPlaygroundStore.js` (registered as
 * `aiPlayground` on `dev`). Holds the AI Playground chat history, the LLM answers generated per
 * message, and the dialog context variables.
 *
 * Only `contextVariables` and `nextCustomId` are persisted to `sessionStorage`, matching the
 * legacy `vuexSession` reducer's `aiPlayground` entry.
 */

/** A single context variable shown/edited in the AI Playground */
export interface ContextVariable {
  /** Context variable name, e.g. "widget_url" or "user_variable_1" */
  key: string;
  /** Value data type: 'String', 'Number' or 'JSON' (JSON values are stored as raw strings and parsed on use) */
  type: string;
  /** Raw value as entered; `null` for value-less channel variables */
  value: string | number | null;
  /** Whether the variable is locked from editing in the UI */
  fixed: boolean;
  /** Whether the variable was created by the user (kept when defaults are re-initialized) */
  custom: boolean;
}

/**
 * A single item in the playground chat history
 * @template T Arbitrary data attached to the item
 */
export interface PlaygroundHistoryItem<T = Record<string, any>> {
  /** A unique UUID for this history item */
  id: string;
  /** The type of history item this is, e.g. 'welcome', 'user', 'result', 'error', 'loading' */
  type: string;
  /** Whether this history item is for admin only */
  admin?: boolean;
  /** Arbitrary data that is attached to this item */
  data: T;
}

/** State of the aiPlaygroundStore module */
export interface AiPlaygroundState {
  /** ID of the current playground conversation */
  conversationId: string;
  /** Message history items */
  history: PlaygroundHistoryItem<any>[];
  /**
   * History of LLM's generated answers per message.
   * A single message may have multiple values if you re-generated it several times.
   * Key: Message index. Value: Generated answers
   */
  generatedAnswerMap: Record<number, any[]>;
  /**
   * Index map of which LLM answer you are viewing per message, used as
   * `generatedAnswerMap[key][value]`. Key: Message index. Value: Generated answer index
   */
  selectedGeneratedAnswerMap: Record<number, number>;
  /** All context variables currently configured in the playground */
  contextVariables: ContextVariable[];
  /** Counter used to generate the next `user_variable_<n>` key */
  nextCustomId: number;
}

/**
 * Context keys that are managed by the RAG response, scoped to a single
 * conversation, and must be stripped on every conversation reset — even when
 * the caller wants to keep their custom context variables.
 */
const CONVERSATION_SCOPED_FOLLOWUP_STATE_KEYS = [
  'narrowing_turns_used',
];

/**
 * Get default context variables as array
 * @param uniqueUserId The user ID for this playground conversation
 */
function getDefaultContextVariables(uniqueUserId: string): ContextVariable[] {
  const browserLanguage = window?.navigator?.language || 'de';
  return [
    {
      key: 'widget_url',
      type: 'String',
      value: window.location.origin,
      fixed: false,
      custom: false,
    },
    {
      key: 'uniqueUserId',
      type: 'String',
      value: uniqueUserId,
      fixed: true,
      custom: false,
    },
    {
      key: 'widget_host',
      type: 'String',
      value: window.location.hostname,
      fixed: false,
      custom: false,
    },
    {
      key: 'conversation_language',
      type: 'String',
      value: browserLanguage,
      fixed: false,
      custom: true,
    },
  ];
}

/**
 * Key for the Playground event bus used with `useEventBus`.
 */
export const PlaygroundEvents: EventBusKey<any> = Symbol('PlaygroundEvents');

export function createPlaygroundConversationId(): string {
  return `playground_${crypto.randomUUID()}`;
}

/**
 * Utility methods that take care of creating the base structures for chat history items.
 */
export const historyItemConstructors = {
  /**
   * Special: A welcome message to the history.
   * This is injected to the beginning of the chat history.
   */
  welcomeMessage(): PlaygroundHistoryItem<{ content: null; items: any[] }> {
    return {
      id: crypto.randomUUID(),
      type: 'welcome',
      data: { content: null, items: [] },
    };
  },

  /**
   * A user-sent message to the history
   * @param text The text to appear in the history
   * @param data Arbitrary data you want to attach to the message
   */
  userMessage(text: string, data: Record<string, any> = {}): PlaygroundHistoryItem {
    return {
      id: crypto.randomUUID(),
      type: 'user',
      data: { ...data, text },
    };
  },

  /**
   * A generic error message item
   * @param title The title of the error. Defaults to a generic title.
   * @param message The description of the error. Defaults to a generic message.
   * @param data Any custom data you want to add a case for in the Error item
   */
  error(title: string | null, message: string | null, data: Record<string, any> = {}): PlaygroundHistoryItem {
    return {
      id: crypto.randomUUID(),
      type: 'error',
      data: { ...data, title, message },
    };
  },

  /**
   * Creates a new loading placeholder, which can be replaced later with another item.
   * @param adminOnly Whether the loading indicator is for admins only
   * @param data Arbitrary data you want to attach to this item
   */
  loading(adminOnly = false, data: Record<string, any> = {}): PlaygroundHistoryItem {
    return {
      id: crypto.randomUUID(),
      admin: adminOnly,
      type: 'loading',
      data: data,
    };
  },

  /**
   * A K2 Indices list. Used by admins to see how intent matching went.
   * @param intents A list of intents that were found
   * @param timeTakenInMS How long it took to find this list
   * @param data Arbitrary data you want to attach to this item
   */
  K2IndicesList(intents: any[] = [], timeTakenInMS = 0, data: Record<string, any> = {}): PlaygroundHistoryItem {
    return {
      id: crypto.randomUUID(),
      type: 'k2',
      admin: true, // Admin-only
      data: {
        ...data,
        intents,
        timeMs: timeTakenInMS,
      },
    };
  },

  /**
   * A detected intent item
   * @param text The text that lead to detecting this intent
   * @param intentName The technical name of the detected intent
   * @param timeTakenInMS How long it took to detect this intent
   * @param data Other arbitrary data you want to add to the item
   */
  detectedIntent(text: string, intentName: string, timeTakenInMS = 0, data: Record<string, any> = {}): PlaygroundHistoryItem {
    return {
      id: crypto.randomUUID(),
      type: 'result',
      data: {
        ...data,
        text,
        intent: intentName,
        timeMs: timeTakenInMS,
      },
    };
  },

  /**
   * A new item that contains search result of RAG Resources that could be suitable for the given intent
   * @param intentName The technical name of the intent that we found candidate resources for
   * @param results The resources that were found to be suitable
   * @param data Arbitrary data to attach to this item
   */
  suitableResourceFound(intentName: string | null | undefined, results: any[], data: Record<string, any> = {}): PlaygroundHistoryItem {
    return {
      id: crypto.randomUUID(),
      type: 'ragSearchResult',
      data: {
        ...data,
        searchResult: results,
        intentName,
      },
    };
  },
};

/** Options for {@link useAiPlaygroundStore}'s `resetAIPlayground` */
export interface ResetPlaygroundOptions {
  /** Keep the Welcome item in the history */
  keepWelcome?: boolean;
  /** Keep the conversation and uniqueUserId */
  keepConversationId?: boolean;
  /** Keep your existing context (uniqueUserId will be updated accordingly) */
  keepContext?: boolean;
}

/**
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - Legacy had BOTH a state prop `contextVariables` and an identity-passthrough getter of the
 *   same name (`(state) => state.contextVariables`). The state prop keeps the name; the getter is
 *   renamed `contextVariablesGetter` (a getter with the same name as a state prop would collide in
 *   Pinia's single-namespace store instance).
 * - The legacy mutations (`SET_CONTEXT_VARIABLES`, `ADD_CONTEXT_VARIABLE`,
 *   `UPDATE_CONTEXT_VARIABLE`, `REMOVE_CONTEXT_VARIABLE`, `CLEAR_CONTEXT_VARIABLES`,
 *   `INCREMENT_CUSTOM_ID`) were never committed directly by any consumer outside this module — only
 *   the actions below were ever dispatched. They are inlined into the action bodies that used to
 *   `commit(...)` them (no separately-named action is created for them).
 */
export const useAiPlaygroundStore = defineStore('aiPlaygroundStore', {
  state: (): AiPlaygroundState => {
    const conversationId = createPlaygroundConversationId();
    return {
      conversationId,
      history: [],
      generatedAnswerMap: {},
      selectedGeneratedAnswerMap: {},
      contextVariables: getDefaultContextVariables(`user_${conversationId}`),
      nextCustomId: 1,
    };
  },
  getters: {
    /** The last item in the chat history */
    lastHistoryItem(): PlaygroundHistoryItem<any> | undefined {
      return this.history.at(-1);
    },
    /**
     * Returns a simple aggregate of the last 10 messages in the conversation so far,
     * pulling only user messages and RAG Answers
     * @returns `(stopIdx = -1) => null | {role, text}[]`
     */
    getConversationSoFar(): (stopIdx?: number) => null | { role: 'User' | 'Assistant'; text: string }[] {
      return (stopIdx = -1) => {
        // No conversation yet
        if (!this.history.length) return null;

        const intentsStore = useIntentsStore();
        const conversation: { role: 'User' | 'Assistant'; text: string }[] = [];
        for (let i = 0; i < this.history.length; i++) {
          if (i === stopIdx) break;
          const item = this.history[i]!;

          switch (item.type) {
            // User-sent text message
            case 'user':
              conversation.push({
                role: 'User',
                text: item.data.text,
              });
              break;
            // Intent detection result
            case 'result': {
              // If it's not a RAGTopic, skip it
              const intent: any = intentsStore.intent(item.data.intent);
              if (!intent || intent.template !== 'RAGTopic') continue;
              const answer = this.getCurrentAnswer(i);

              // If it doesn't have answer generated yet, skip it
              if (!answer) continue;

              const texts: string[] = [];

              // We must iterate over answers given, which can be meta info or actual answers
              for (const a of answer.answers) {
                if (!a.answer?.text) continue;
                texts.push(a.answer.text);
              }

              if (texts.length) {
                conversation.push({
                  role: 'Assistant',
                  text: texts.join('\n'),
                });
              }
              break;
            }
            // Special: Welcome message. Should only appear once
            case 'welcome': {
              /**
               * This is the Welcome CX Content, parsed and broken down by parts.
               * It can be e.g. images, slides, etc., but we want the text parts
               * only for the conversation history.
               */
              const textParts = item.data?.items
                ?.filter((t: any) => t.type === 'text')
                ?.map((t: any) => t.value)
                ?.filter(Boolean)
                ?.join('\n')
                ?.trim() || '';
              if (!textParts) break;

              conversation.push({
                role: 'Assistant',
                text: textParts,
              });
              break;
            }
          }
        }

        // The most recent user message in `history` is always the input we
        // are about to process — it belongs in the request as the current turn
        // (text / user_request), not in the previousConversation. Strip any
        // trailing User entries so the API does not see the same message twice.
        while (conversation.length && conversation.at(-1)!.role === 'User') {
          conversation.pop();
        }

        // Last 10, because that's what the backend does by default
        return conversation.slice(-10);
      };
    },
    /**
     * How many answers have been generated for the given message at index
     * @returns 0 if no records found
     */
    generatedAnswerCountForMessageIdx(): (messageIdx: number) => number {
      return (messageIdx: number) => this.generatedAnswerMap[messageIdx]?.length ?? 0;
    },
    /** The generated answer currently selected for the message at index, or null */
    getCurrentAnswer(): (messageIndex: number) => any | null {
      return (messageIndex: number) => {
        const answersFor = this.generatedAnswerMap[messageIndex];
        if (!answersFor) return null;
        return answersFor[this.selectedGeneratedAnswerMap[messageIndex]!] ?? null;
      };
    },
    getContextVariableValue(): (key: string) => any {
      return (key: string) => this.contextVariables.find(v => v.key === key)?.value;
    },
    /** Returns a deep copy of the exportable state */
    exportState(): () => Omit<AiPlaygroundState, never> {
      return () => JSON.parse(JSON.stringify({
        conversationId: this.conversationId,
        history: this.history,
        generatedAnswerMap: this.generatedAnswerMap,
        selectedGeneratedAnswerMap: this.selectedGeneratedAnswerMap,
        contextVariables: this.contextVariables,
        nextCustomId: this.nextCustomId,
      }));
    },
    /**
     * Renamed from legacy getter `contextVariables` (collides with the state prop of the same
     * name — the state prop keeps the name).
     */
    contextVariablesGetter(): ContextVariable[] {
      return this.contextVariables;
    },
    getContextVariableByIndex(): (index: number) => ContextVariable {
      return (index: number) => this.contextVariables[index];
    },
    hasContextVariable(): (key: string) => boolean {
      return (key: string) => this.contextVariables.some(v => v.key === key);
    },
    dialogContext(): { context: { name: string; pushed: string; parameters?: any }[] } {
      const currentTime = new Date().toISOString();

      return {
        context: this.contextVariables.map(variable => {
          let value: any = variable.value;

          // Parse JSON variables for API calls
          if (variable.type === 'JSON') {
            try {
              value = JSON.parse(variable.value as string);
            } catch (error) {
              value = {}; // Fallback to empty object for invalid JSON
            }
          }

          const contextItem: { name: string; pushed: string; parameters?: any } = {
            name: variable.key,
            pushed: currentTime,
          };

          // Only add parameters if the value is not undefined/null and not an empty string
          if (value !== undefined && value !== null && value !== '') {
            contextItem.parameters = value;
          }

          return contextItem;
        }),
      };
    },
  },
  actions: {
    /**
     * Replaces an existing history item with the new item you provided.
     * @returns `true` if it was swapped, `false` if it was not found and could not swap
     */
    replaceItem(existingItem: Partial<PlaygroundHistoryItem> & { id: string }, newItem: PlaygroundHistoryItem): boolean {
      if (!newItem) {
        console.error('New item was not defined');
        return false;
      }
      if (!newItem.id || !newItem.type) {
        console.error('New item must have both an ID and a type');
        return false;
      }

      const idx = this.history.findIndex((item) => item.id === existingItem.id);
      if (idx === -1) return false;

      this.history.splice(idx, 1, newItem);
      return true;
    },
    /** Add a new history item */
    pushItem(newItem: PlaygroundHistoryItem) {
      if (!newItem) return console.error('New item was not defined');
      if (!newItem.id || !newItem.type) return console.error('New item must have both an ID and a type');
      this.history.push(newItem);
    },
    /** Merges `data` into the data of the history item with the given ID */
    setItemData(id: string, data: Record<string, any>) {
      const item = this.history.find((i) => i.id === id);
      if (item) Object.assign(item.data, data);
    },
    setConversationId(conversationId: string) {
      this.conversationId = conversationId;
    },
    /**
     * Special: Sets the welcome message. This is injected to the beginning of the chat history.
     */
    setWelcomeItem(welcomeItem: PlaygroundHistoryItem) {
      this.history.unshift(welcomeItem);
    },
    /**
     * Clears the history
     */
    resetAIPlayground(options: ResetPlaygroundOptions = {}) {
      const welcome = this.history[0];
      this.history = [];

      // Preserve welcome (avoids needing to re-fetch data)
      if (options.keepWelcome && welcome) this.history.push(welcome);

      // New conversation and user ID
      if (!options.keepConversationId) {
        this.conversationId = createPlaygroundConversationId();
      }

      // Reset or update context
      if (options.keepContext && !options.keepConversationId) {
        const ctx = this.contextVariables.find((c) => c.key === 'uniqueUserId');
        if (ctx) ctx.value = `user_${this.conversationId}`;
      } else if (!options.keepContext) {
        this.contextVariables = getDefaultContextVariables(`user_${this.conversationId}`);
      }

      // Followup-state vars (narrowing budget/used, output_gate_reached, ...) are
      // scoped to a single conversation. Always strip them on reset, even when
      // the user wants to keep their custom context — otherwise they leak into
      // the next conversation and bias the new turn.
      if (!options.keepConversationId) {
        this.contextVariables = this.contextVariables.filter(
          (ctx) => !CONVERSATION_SCOPED_FOLLOWUP_STATE_KEYS.includes(ctx.key),
        );
      }

      this.selectedGeneratedAnswerMap = {};
      this.generatedAnswerMap = {};
    },
    /**
     * Stores an LLM's generated answer for a given message by index.
     * Automatically selects the answer you pushed.
     * @param payload.messageIndex Index of the message in the `history[]`
     * @param payload.data Whatever data you need for the component or otherwise
     */
    pushGeneratedAnswer({ messageIndex, data }: { messageIndex: number; data: any }) {
      const generatedAnswers = this.generatedAnswerMap[messageIndex] || [];
      generatedAnswers.push(data);

      this.generatedAnswerMap[messageIndex] = generatedAnswers;
      this.selectedGeneratedAnswerMap[messageIndex] = generatedAnswers.length - 1;
    },
    selectGeneratedAnswer({ messageIndex, answerIndex }: { messageIndex: number; answerIndex: number }) {
      this.selectedGeneratedAnswerMap[messageIndex] = answerIndex;
    },
    /** Replaces the playground state with previously exported data (see `exportState`) */
    importState(data: Pick<AiPlaygroundState, 'conversationId' | 'history' | 'generatedAnswerMap' | 'selectedGeneratedAnswerMap' | 'contextVariables' | 'nextCustomId'>) {
      this.conversationId = data.conversationId;
      this.history = data.history;
      this.generatedAnswerMap = data.generatedAnswerMap;
      this.selectedGeneratedAnswerMap = data.selectedGeneratedAnswerMap;
      this.contextVariables = data.contextVariables;
      this.nextCustomId = data.nextCustomId;
    },

    /**
     * Merged from legacy action `updateContextVariables` + legacy mutation
     * `SET_CONTEXT_VARIABLES` (the action only committed the mutation).
     */
    updateContextVariables(variables: ContextVariable[]) {
      this.contextVariables = [...variables];
    },

    /**
     * Merged from legacy action `addContextVariable` + legacy mutation `ADD_CONTEXT_VARIABLE`
     * (the action only committed the mutation).
     */
    addContextVariable(variable: ContextVariable) {
      this.contextVariables.push(variable);
    },

    /**
     * Merged from legacy action `updateContextVariable` + legacy mutation
     * `UPDATE_CONTEXT_VARIABLE` (the action only committed the mutation).
     */
    updateContextVariable({ index, variable }: { index: number; variable: ContextVariable }) {
      if (index >= 0 && index < this.contextVariables.length) {
        this.contextVariables.splice(index, 1, variable);
      }
    },

    /**
     * Merged from legacy action `addCustomVariable` + legacy mutations `ADD_CONTEXT_VARIABLE` and
     * `INCREMENT_CUSTOM_ID` (the action only committed those mutations).
     */
    addCustomVariable(): string {
      const variable: ContextVariable = {
        key: `user_variable_${this.nextCustomId}`,
        type: 'String',
        value: '',
        fixed: false,
        custom: true,
      };

      this.contextVariables.push(variable);
      this.nextCustomId++;

      return variable.key; // Return the key so component can open the panel
    },

    /**
     * Merged from legacy action `removeContextVariable` + legacy mutation
     * `REMOVE_CONTEXT_VARIABLE` (the action only committed the mutation).
     */
    removeContextVariable(index: number) {
      if (index >= 0 && index < this.contextVariables.length) {
        this.contextVariables.splice(index, 1);
      }
    },

    /**
     * Merged from legacy action `clearContextVariables` + legacy mutation
     * `CLEAR_CONTEXT_VARIABLES` (the action only committed the mutation).
     */
    clearContextVariables() {
      this.contextVariables = getDefaultContextVariables(`user_${this.conversationId}`);
    },

    /**
     * Merged from legacy action `initializeDefaultVariables` + legacy mutation
     * `SET_CONTEXT_VARIABLES` (the action only committed the mutation).
     */
    initializeDefaultVariables() {
      const defaultVars = getDefaultContextVariables(`user_${this.conversationId}`);
      // Merge with existing custom variables
      const existingCustomVars = this.contextVariables.filter((v: ContextVariable) => v.custom);
      const mergedVariables = [...defaultVars, ...existingCustomVars];
      this.contextVariables = mergedVariables;
    },

    /**
     * Merged from legacy action `updateChannelVariables` + legacy mutation
     * `SET_CONTEXT_VARIABLES` (the action only committed the mutation).
     */
    updateChannelVariables({ channelId, channelType }: { channelId?: string | null; channelType?: string | null }) {
      // Remove old channel variables
      const filteredVariables = this.contextVariables.filter((v: ContextVariable) => !v.key.startsWith('channel_'));

      // Prepare new channel variables to prepend
      const newChannelVariables: ContextVariable[] = [];

      if (channelType) {
        newChannelVariables.push({
          key: `channel_${channelType}`,
          type: 'String',
          value: null,
          fixed: true,
          custom: false,
        });
      }

      if (channelId) {
        newChannelVariables.push({
          key: `channel_${channelId}`,
          type: 'String',
          value: null,
          fixed: true,
          custom: false,
        });
      }

      // Prepend channel variables to the beginning of the array
      const updatedVariables = [...newChannelVariables, ...filteredVariables];

      this.contextVariables = updatedVariables;
    },
  },
  vuexPersist: {
    session: ['contextVariables', 'nextCustomId'],
  },
});
